import { Localized, useLocalization } from "@fluent/react"
import { alpha, createTheme, ThemeProvider, useTheme } from "@mui/material/styles"
import { Avatar, Chip, ListItemAvatar } from "@mui/material"
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import CloseIcon from '@mui/icons-material/Close'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import { ChangeEvent, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { MeetingMinute, MeetingMinuteItem, ReportRequestParameters, deleteMeetingMinuteItem, loadMeetingMinutes, resumeMeeting, sendGeneralReport, suspendMeeting } from "../../project/mettingMinutes/meetingMinutesSlice"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { unwrapResult } from "@reduxjs/toolkit"
import { APIError } from "../../../app/appTypes"
import { Alert, Box, Button, ButtonBase, ButtonGroup, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, MenuItem, Paper, Skeleton, Stack, Switch, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, List, ListItem, ListItemText, ListSubheader, DialogContentText, ToggleButtonGroup, ToggleButton, Tooltip } from "@mui/material"
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab"
import { DataGrid, GridColDef, useGridApiRef } from "@mui/x-data-grid"
import { If } from "../../../app/If"
import AddIcon from '@mui/icons-material/Add'
import produce from "immer"
import { Attachements } from "../../urs/Attachements"
import { BlobResourceType } from "../../documents/blobResourceTypes"
import { loadManyBlobs } from "../../documents/attachementsSlice"
import { MeetingMinutesItemStatusLabel } from "../../project/mettingMinutes/MeetingMinutesItemStatusLabel"
import { CreateMeetingMinutesItem } from "../../project/mettingMinutes/dialogs/CreateMeetingMinutesItem"
import { MeetingMinutesItemPriorityLabel } from "../../project/mettingMinutes/MeetingMinutesItemPriorityLabel"
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import React from "react"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TaskIcon from '@mui/icons-material/Task'
import ImportContactsIcon from '@mui/icons-material/ImportContacts'
import { AddItemTaskDialog } from "../../project/mettingMinutes/dialogs/AddItemTask"
import { addAttendeeToMeeting, Attendee, deleteAttendeeFromMeeting, Meeting } from "../../project/mettingMinutes/meeting"
import { format, parseISO } from "date-fns"
import { parseAndFormatISODateWithTodayCompare, validateEmail as validateEmailUtil } from "../../../app/Utils"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { selectSettings } from "../../settings/settingsSlice"
import DeleteIcon from '@mui/icons-material/Delete'
import { Email, UsersBrowser } from "../../users/UsersBrowser"
import RemoveIcon from '@mui/icons-material/Remove'
import { MeetingMinutesStatistics } from "../../project/mettingMinutes/MeetingMinutesStatistics"
import { ProjectProcessHeader, loadForProject } from "../../project/projectProcess/ProjectProcessApiClient"
import EmailInput from "../../project/mettingMinutes/components/EmailInput"
import EmailIcon from '@mui/icons-material/Email'
import { ErrorContainer } from "../../../app/ErrorContainer"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { MeetingMinutesReportsDropdown } from "../../project/mettingMinutes/components/MeetingMinutesReportsDropdown"
import { ReportPrinter } from "../../common/ReportPrinter"
import ViewListIcon from '@mui/icons-material/ViewList'
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AreaStatusChip } from "../components/OperationalDashboardAreaPanel"
import { NewMeetingItemDetails, mergeMeetingItemDetails } from "./NewMeetingItemDetails"
import type { NewMeetingItemDetailsHandle } from "./NewMeetingItemDetails"
import { CloseMeetingDialog } from "../../project/mettingMinutes/dialogs/CloseMeetingDialog"
import { NewMeetingMinutesHeader } from "./NewMeetingMinutesHeader"
import { usePackage } from "../../permissions/usePackage";

type FormTabs = "items" | "attachments" | "meetings" | "attendees" | "statistics"

const RequiredError = () => <Localized id="field-is-required"><span>Pole jest wymagane</span></Localized>

export const NewMeetingMinutesForm = () => {
  const dispatch = useAppDispatch()
  const { id: paramId } = useParams(); const id = paramId ?? ''
  const [meetingMinute, setMeetingMinute] = useState<MeetingMinute>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingError, setLoadingError] = useState<APIError | undefined>(undefined)
  const [loadingErrorOccured, setLoadingErrorOccured] = useState<boolean>(false)

  useEffect(() => {
    setIsLoading(true);
    dispatch(loadMeetingMinutes(id))
      .then(unwrapResult)
      .then(setMeetingMinute)
      .catch((error) => { console.log(error); setLoadingErrorOccured(true); setLoadingError(error); })
      .finally(() => setIsLoading(false));
  }, [])

  if (loadingErrorOccured) {
    return <>
      <ErrorContainer error={loadingError} />
    </>
  }

  if (isLoading) {
    return <>
      <LoadingContainer />
    </>
  }

  if (meetingMinute === undefined) {
    return <Skeleton animation="wave" variant="rectangular" height="64vh" ></Skeleton>;
  }

  return <>
    <EditForm id={id} meetingMinute={meetingMinute} setMeetingMinute={setMeetingMinute} />
  </>
}

type DeleteState
  = { tag: 'none' }
  | { tag: 'delete', id: number }


const EditForm = (props: { id: string, meetingMinute: MeetingMinute, setMeetingMinute: React.Dispatch<SetStateAction<MeetingMinute | undefined>> }) => {
  const { meetingMinute, setMeetingMinute } = props
  const { l10n } = useLocalization()
  const tr = (key: string, fallback: string) => l10n.getString(key, null, fallback)
  const parentTheme = useTheme()
  const meetingTheme = useMemo(() => {
    const dark = parentTheme.palette.mode === 'dark'
    return createTheme(parentTheme, {
      palette: {
        primary: dark
          ? { main: '#8ACDBB', dark: '#6DB9A5', light: '#B9E3D6', contrastText: '#132E26' }
          : { main: '#237E72', dark: '#1B655B', light: '#E3F2ED', contrastText: '#FFFFFF' },
        background: { default: dark ? '#182321' : '#F3F8F7', paper: dark ? '#202E2B' : '#FFFFFF' },
        text: { primary: dark ? '#E7F2EE' : '#203B35', secondary: dark ? '#ADC5BC' : '#627B73' },
        divider: dark ? '#394E47' : '#DCE8E3',
      },
    })
  }, [parentTheme])
  const seaSurface = meetingTheme.palette.mode === 'dark' ? '#293F37' : '#E3F2ED'
  const neutralSurface = alpha(meetingTheme.palette.text.primary, 0.035)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { locale } = useAppSelector(selectSettings);

  const [processes, setProcesses] = useState<ProjectProcessHeader[]>([])

  const canWrite = usePackage("MeettingMinutes", "ReadWrite")
  const isEditable = canWrite && meetingMinute.status === "INMEETING"
  const isOperationalDashboard = meetingMinute.parentObjectType === "OPERATIONAL_DASHBOARD"
  const dashboardGuid = isOperationalDashboard ? meetingMinute.parentObjectGuid?.trim() : undefined
  const [printDialogOpen, setPrintDialogOpen] = useState(false)
  const [printWithHistory, setPrintWithHistory] = useState(false)

  const allItems = meetingMinute.items || []
  const lastMeeting = meetingMinute.meetings.find(x => x.id === meetingMinute.lastMeetingId)
  const agendaItems = [...(lastMeeting?.agendaItems ?? [])].sort((a, b) => a.order - b.order || a.id - b.id)
  const agendaItemIdParam = searchParams.get("agendaItemId")
  const requestedAgenda = agendaItemIdParam && /^[1-9]\d*$/.test(agendaItemIdParam)
    ? agendaItems.find(item => item.id === Number(agendaItemIdParam)) : undefined
  const requestedAgendaGuid = requestedAgenda?.sharedGuid
  const defaultAgendaGuid = agendaItems[0]?.sharedGuid ?? null
  const [error, setError] = useState<APIError | null>(null)

  const [tab, setTab] = useState<FormTabs>("items");
  const [selectedAgendaGuid, setSelectedAgendaGuid] = useState<string | null>(requestedAgendaGuid ?? defaultAgendaGuid);
  useEffect(() => {
    setSelectedAgendaGuid(requestedAgendaGuid ?? defaultAgendaGuid)
  }, [agendaItemIdParam, requestedAgendaGuid, defaultAgendaGuid])
  const activeAgendaGuid = agendaItems.find(item => item.sharedGuid.toLowerCase() === selectedAgendaGuid?.toLowerCase())?.sharedGuid
    ?? agendaItems[0]?.sharedGuid ?? null;
  const activeAgendaIndex = activeAgendaGuid === null ? agendaItems.length
    : agendaItems.findIndex(item => item.sharedGuid.toLowerCase() === activeAgendaGuid.toLowerCase());
  const [createItemDialogVisible, setCreateItemDialogVisible] = useState(false);
  const [showHighPriority, setShowHighPriority] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [selectedProcessId, setSelectedProcessId] = useState<string | undefined>(undefined)
  const [manualAttendeeOpen, setManualAttendeeOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'flat' | 'grouped'>('flat');

  const [attachmentLoaded, setAttachmentLoaded] = useState(false);
  const [processesLoaded, setProcessesLoaded] = useState(false);

  const [listOfRecipients, setListOfRecipients] = useState<string[]>([]);
  const [attachNoteAttachments, setAttachNoteAttachments] = useState(true);
  const [reportRequestParameters, setReportRequestParameters] = useState<ReportRequestParameters>({
    reportType: isOperationalDashboard ? 'OPERATIONAL_REPORT' : 'MEETING',
    reportId: null,
    reportSource: 'MeetingMinutes',
    language: 'pl',
    isDynamicReport: false,
    onlyCurrentMeeting: true,
    includeFullItemsHistory: true,
    selectedStatuses: [],
    selectedPriorities: [],
    selectedProcesses: []
  });

  const handleListOfRecipientsChanged = (listOfRecipients: string[]) => {
    setListOfRecipients(listOfRecipients);
  };

  const handleAttachNoteAttachmentsChanged = (attach: boolean) => {
    setAttachNoteAttachments(attach);
  };

  const [saving, setSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState(false)
  const [person, setPerson] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [deleteAttendee, setDeleteAttendee] = useState<DeleteState>({ tag: 'none' })

  const [appUsersDialogOpen, setAppUsersDialogOpen] = useState(false)
  const attendees = meetingMinute.meetings.find(x => x.id === meetingMinute.lastMeetingId)?.attendanceList ?? []

  const usersGridRef = useGridApiRef()

  const attendeesPersonError = validationErrors && (person === "" || person === null)
  const attendeesCompanyError = validationErrors && (company === "" || company === null)

  const findProcessName = (projectProcessId) => {
    if (processes && projectProcessId) {
      const process = processes.find(p => p.id.toString() === projectProcessId.toString());
      return process ? process.name : '';
    }
    return '';
  };

  const handleConfirmDeleteAttendee = () => {
    if (deleteAttendee.tag === 'delete') {
      const { id } = deleteAttendee
      dispatch(deleteAttendeeFromMeeting(id))
        .then(unwrapResult)
        .then(() => {
          setMeetingMinute(produce(draft => {
            const meeting = draft?.meetings.find(x => x.id === draft?.lastMeetingId)
            if (meeting) {
              meeting.attendanceList = meeting.attendanceList.filter(x => x.id !== id)
            }
          }))
          setDeleteAttendee({ tag: 'none' })
        }).catch((error) => {
          setDeleteAttendee({ tag: 'none' })
          const payload = error as APIError
          if (payload) {
            if (payload.kind === 'http') {
              const problem = payload.problem
              if (problem) {
                dispatch(showError(problem.title))
              }
            }
          }
        })
    }
  }
  const handleAddAttendeeToMeeting = () => {
    if (person && company && !emailError) {
      setSaving(true);
      setValidationErrors(true);

      dispatch(addAttendeeToMeeting({
        id: 0,
        person,
        company,
        email,
        meetingId: meetingMinute.lastMeetingId ?? 0
      }))
        .then(unwrapResult)
        .then((attendee) => {
          setMeetingMinute(produce(draft => {
            const meeting = draft?.meetings.find(x => x.id === draft?.lastMeetingId)
            if (meeting) {
              meeting.attendanceList.push(attendee)
            }
          }

          ))
          setPerson('')
          setCompany('')
          setEmail('')
        }).finally(() => {
          setSaving(false);
          setValidationErrors(false);
        })
    }

  }
  const handleAddAppUsers = () => {
    setAppUsersDialogOpen(false)

    if (usersGridRef.current) {
      setSaving(true)
      usersGridRef.current.getSelectedRows().forEach((row) => {
        dispatch(addAttendeeToMeeting({
          id: 0,
          person: row.firstname + ' ' + row.surname,
          company: row.companyName,
          email: row.email,
          meetingId: meetingMinute.lastMeetingId ?? 0,
        })).then(unwrapResult)
          .then((attendee) => {
            setMeetingMinute(produce(draft => {
              const meeting = draft?.meetings.find(x => x.id === draft?.lastMeetingId)
              if (meeting) {
                meeting.attendanceList.push(attendee)
              }
            }))
          }).finally(() => {
            setSaving(false)
            dispatch(showSuccess("saved"));
          })
      })
    }
  }


  const handleSelectedProcessChange: (e: ChangeEvent<HTMLInputElement>) => void =
    useMemo(() =>
      (e) => setSelectedProcessId(e.target.value), [setSelectedProcessId]);


  const handleShowHighPriorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHighPriority(event.target.checked);
  };

  const handleShowActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowActiveOnly(event.target.checked);
  };

  const [showSendGeneralReportDialog, setShowSendGeneralReportDialog] = useState(false);

  const handleShowSendReportDialog = useCallback(() => {
    setShowSendGeneralReportDialog(true)
  }, [setShowSendGeneralReportDialog])

  const handleCancelSendGeneralReportClick = useCallback(() => {
    setShowSendGeneralReportDialog(false)
  }, [])

  const handleConfirmSendGeneralReport = async () => {
    if (meetingMinute) {
      setSaving(true)
      try {
        const result =
          await dispatch(sendGeneralReport(
            {
              meetingMinuteId: meetingMinute.id,
              listOfRecipients: listOfRecipients,
              attachNoteAttachments: attachNoteAttachments,
              language: locale,
              reportRequestParameters: reportRequestParameters
            }
          ))
        dispatch(showSuccess("sent"))
      }
      catch (error) {
        dispatch(showError("error"))
      }
      finally {
        setSaving(false)

        setShowSendGeneralReportDialog(false)
      }
    }
  }

  const [showCloseMeetingDialog, setShowCloseMeetingDialog] = useState(false);
  const [showSuspendMeetingDialog, setShowSuspendMeetingDialog] = useState(false);
  const [showResumeMeetingDialog, setShowResumeMeetingDialog] = useState(false);

  const handleCloseMeetingClick = useCallback(() => {
    setShowCloseMeetingDialog(true)
  }, [setShowCloseMeetingDialog])


  const handleSuspendMeetingClick = useCallback(() => {
    setShowSuspendMeetingDialog(true)
  }, [setShowSuspendMeetingDialog])

  const handleResumeMeetingClick = useCallback(() => {
    setShowResumeMeetingDialog(true)
  }, [setShowResumeMeetingDialog])



  const handleConfirmResumeMeetingClick = async () => {
    setSaving(true)
    if (meetingMinute) {
      try {
        const result =
          await dispatch(resumeMeeting(
            {
              meetingMinuteId: meetingMinute.id
            }
          ))

        if (resumeMeeting.fulfilled.match(result)) {
          const a = result.payload as MeetingMinute;
          setMeetingMinute(a)
          dispatch(showSuccess("meeting-minutes-resumed-meeting"))
        }
        else {
          let errorMsg = "error"
          if (result.payload) {
            if (result.payload.kind === 'http') {
              const problem = result.payload.problem
              if (problem) {
                errorMsg = problem.title
              }
            }
          }
          dispatch(showError(errorMsg))
        }
      }
      catch (error) {
        dispatch(showError("error"))
      }
      finally {
        setSaving(false)
      }
    }
    setShowResumeMeetingDialog(false)
    setSaving(false)
  }

  const handleConfirmSuspendMeetingClick = async () => {
    if ((await detailsRef.current?.flush()) === false) return
    setSaving(true)
    if (meetingMinute) {
      try {
        const result =
          await dispatch(suspendMeeting(
            {
              meetingMinuteId: meetingMinute.id
            }
          ))

        if (suspendMeeting.fulfilled.match(result)) {
          const a = result.payload as MeetingMinute;
          setMeetingMinute(a)
          dispatch(showSuccess("meeting-minutes-suspended-meeting"))
        }
        else {
          let errorMsg = "error"
          if (result.payload) {
            if (result.payload.kind === 'http') {
              const problem = result.payload.problem
              if (problem) {
                errorMsg = problem.title
              }
            }
          }
          dispatch(showError(errorMsg))
        }
      }
      catch (error) {
        dispatch(showError("error"))
      }
      finally {
        setSaving(false)
      }
    }
    setShowSuspendMeetingDialog(false)
    setSaving(false)
  }

  const handleResumeCloseMeetingClick = useCallback(() => {
    setShowResumeMeetingDialog(false)
  }, [])

  const handleSuspenCloseMeetingClick = useCallback(() => {
    setShowSuspendMeetingDialog(false)
  }, [])

  const handlePrintReportClick = async () => {
    if (meetingMinute) {
      window.open(`${process.env.REACT_APP_REST_API}api/reports/meeting-minutes/general/${meetingMinute.id}?language=${locale}`, '_blank')
    }
  }

  const handlePrintOperationalReportClick = async (withHistory = false) => {
    if ((await detailsRef.current?.flush()) === false) return
    setPrintWithHistory(withHistory)
    setPrintDialogOpen(true)
  }

  const handlePrintMeetingReportClick = async () => {
    if (meetingMinute) {
      window.open(`${process.env.REACT_APP_REST_API}api/reports/meeting-minutes/lastmeeting/${meetingMinute.id}?language=${locale}`, '_blank')
    }
  }

  const handlePrintMinimalMeetingReportClick = async () => {
    if (meetingMinute) {
      window.open(`${process.env.REACT_APP_REST_API}api/reports/meeting-minutes/minimal/${meetingMinute.id}?language=${locale}`, '_blank')
    }
  }

  const handlePrintOnlyactiveMeetingReportClick = async () => {
    if (meetingMinute) {
      window.open(`${process.env.REACT_APP_REST_API}api/reports/meeting-minutes/onlyactive/${meetingMinute.id}?language=${locale}`, '_blank')
    }
  }

  const handlePrintNohistoryMeetingReportClick = async () => {
    if (meetingMinute) {
      window.open(`${process.env.REACT_APP_REST_API}api/reports/meeting-minutes/nohistory/${meetingMinute.id}?language=${locale}`, '_blank')
    }
  }



  const detailsRef = useRef<NewMeetingItemDetailsHandle>(null)

  const handleTabChange = useCallback(async (_, newTab: FormTabs) => {
    if ((await detailsRef.current?.flush()) === false) return
    setTab(newTab)
  }, [])

  useEffect(() => {
    if (meetingMinute.parentObjectGuid && !processesLoaded) {
      dispatch(loadForProject(meetingMinute.parentObjectGuid))
        .then(unwrapResult)
        .then(setProcesses)
        .then(() => { setProcessesLoaded(true) })
        .catch((err) => setError(err))
    }
  }, [meetingMinute])

  useEffect(() => {
    if (!attachmentLoaded && meetingMinute) {
      dispatch(loadManyBlobs([meetingMinute.id]))
        .then(unwrapResult)
        .then(() => { setAttachmentLoaded(true) })
    }
  }, [meetingMinute, attachmentLoaded])

  const openCreateItemDialog = useCallback(() => {
    setCreateItemDialogVisible(true)
  }, [setCreateItemDialogVisible])

  const handleBackButtonClick = useCallback(async () => {
    if ((await detailsRef.current?.flush()) === false) return
    navigate(-1)
  }, [navigate])

  const handleItemSaved = useCallback((saved: MeetingMinuteItem) => {
    setMeetingMinute(produce(draft => {
      if (!draft) return
      if (saved.itemType === "TASK") {
        const parent = draft.items.find(item => item.id === saved.parentItemId)
        const index = parent?.tasks.findIndex(task => task.id === saved.id) ?? -1
        if (parent && index >= 0) {
          parent.tasks[index] = mergeMeetingItemDetails(parent.tasks[index], saved)
        }
      } else {
        const index = draft.items.findIndex(item => item.id === saved.id)
        if (index >= 0) {
          draft.items[index] = mergeMeetingItemDetails(draft.items[index], saved)
        }
      }
    }))
    setSelectedRow(current => current?.id === saved.id && current.itemType === saved.itemType
      ? mergeMeetingItemDetails(current, saved)
      : current)
  }, [setMeetingMinute])

  const onSuccess = useCallback((item: MeetingMinuteItem) => {
    setMeetingMinute(produce(draft => {
      if (draft !== undefined) {
        if (item.itemType == "TASK") {
          const index = draft.items.findIndex(u => u.id === item.parentItemId)
          if (index >= 0) {
            const taskIndex = draft.items[index].tasks.findIndex(x => x.id == item.id)
            if (taskIndex >= 0) {
              draft.items[index].tasks[taskIndex] = item
            }
            else {
              draft.items[index].tasks.push(item)
            }
          }
        } else {
          const index = draft.items.findIndex(u => u.id === item.id)
          if (index >= 0) {
            draft.items[index] = item
          } else {
            draft.items.push(item)
          }
        }
      }
    }))
    setCreateItemDialogVisible(false)
    setCreateTaskDialogVisible(false)
    handleRowClick(item)
  }, [dispatch])


  const handleHideCreateDialog = useCallback(() => setCreateItemDialogVisible(false), [])

  // -----------------------------
  // Edit Item SECTION
  // -----------------------------
  const [detailPanelOpen, setDetailPanelOpen] = useState(true)

  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<MeetingMinuteItem>();

  const selectAgenda = (guid: string) => {
    if (guid === activeAgendaGuid) return
    setSelectedAgendaGuid(guid)
    setSelectedRowId(null)
    setSelectedRow(undefined)
  }

  const handleRowClick = (row: MeetingMinuteItem) => {
    setDetailPanelOpen(true)
    setSelectedRowId(row.id)
    setSelectedRow(row)
  };

  // Funkcja do obsługi kliknięcia przycisku expand/collapse
  const handleExpandClick = (id: string) => {
    const isRowCurrentlyExpanded = expandedRows.includes(id);
    setExpandedRows(isRowCurrentlyExpanded ?
      expandedRows.filter(expandedId => expandedId !== id) : // Usuwa ID z tablicy, jeśli jest już rozwinięte
      [...expandedRows, id] // Dodaje ID do tablicy, jeśli jest zwiniete
    );
  };

  const [createTaskDialogVisible, setCreateTaskDialogVisible] = useState(false)
  const [deleteItemDialogVisible, setDeleteItemDialogVisible] = useState(false)

  const handleCancelDelClick = useCallback(() => {
    setDeleteItemDialogVisible(false)
  }, [setDeleteItemDialogVisible])


  const handleConfirmDelItemClick = async () => {
    if (selectedRow) {
      if ((await detailsRef.current?.flush()) === false) return
      setSaving(true)
      try {
        const result = await dispatch(deleteMeetingMinuteItem(selectedRow.id))

        if (deleteMeetingMinuteItem.fulfilled.match(result)) {
          dispatch(showSuccess("deleted"))
          setSelectedRowId(null)
          setSelectedRow(undefined)

          setMeetingMinute(produce(draft => {
            if (draft !== undefined && selectedRow) {
              if (selectedRow.itemType === "TASK") {
                const index = draft.items.findIndex(u => u.id === selectedRow.parentItemId)
                if (index >= 0) {
                  const taskIndex = draft.items[index].tasks.findIndex(x => x.id == selectedRow.id)
                  if (taskIndex >= 0) {
                    draft.items[index].tasks.splice(taskIndex, 1)
                  }
                }

              }
              else {
                const index = draft.items.findIndex(u => u.id === selectedRow.id)
                if (index >= 0) {
                  draft.items.splice(index, 1)
                }
              }
            }
          }))
        }
        else {
          let errorMsg = "error"
          if (result.payload) {
            if (result.payload.kind === 'http') {
              const problem = result.payload.problem
              if (problem) {
                errorMsg = problem.title
              }
            }
          }
          dispatch(showError(errorMsg))
        }
      }
      catch (error) {
        dispatch(showError("error"))
      }
      finally {
        setSaving(false)
        setDeleteItemDialogVisible(false)
      }

      setSaving(false)
      setDeleteItemDialogVisible(false)
    }
  }


  const handleAddTaskCancel = useCallback(() => setCreateTaskDialogVisible(false), [])


  const renderGroupedTable = () => (
    <TableContainer component={Paper} sx={{ maxHeight: { xs: 420, xl: 620 } }}>
      <Table stickyHeader size="small" sx={{ minWidth: 740 }}>
        <TableHead>
          <TableRow>
            <TableCell id="bt" />
            <TableCell id="type" padding="none">
              <Localized id='type'>Typ</Localized>
            </TableCell>
            <TableCell id="title" sx={{ paddingLeft: "12px" }}>
              <Localized id='meeting-minutes-topic-issue'>Temat/Problem</Localized>
            </TableCell>
            <TableCell id="Termin" sx={{ minWidth: "150px", width: "150px", maxWidth: "150px" }}>
              <Localized id='deadline'>Termin</Localized>
            </TableCell>
            <TableCell id="Odpowiedzialny" sx={{ minWidth: "150px", width: "150px", maxWidth: "150px" }}>
              <Localized id='responsible'>Odpowiedzialny</Localized>
            </TableCell>
            <TableCell id="priority" sx={{ minWidth: 84, width: 84, px: 1 }}>
              <Localized id="status">Status</Localized>
            </TableCell>
            <TableCell id="status" sx={{ minWidth: 84, width: 84, px: 1 }}>
              <Localized id="priority">Priorytet</Localized>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {groupedByProcess.map(([processId, rows]) => (
            <React.Fragment key={processId}>
              <TableRow sx={{ backgroundColor: neutralSurface }} >
                <TableCell colSpan={8} size="small" padding="none">
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => toggleGroupExpand(String(processId))}
                      sx={{ mr: 1 }}
                    >
                      {expandedGroups.includes(String(processId)) ? (
                        <ExpandMoreIcon fontSize="small" />
                      ) : (
                        <ChevronRightIcon fontSize="small" />
                      )}
                    </IconButton>

                    <Typography fontWeight="bold" color="text.secondary">
                      {findProcessName(processId) || l10n.getString("meeting-minutes-other-topics", null, "_Other topics")}
                    </Typography>
                    {!findProcessName(processId) && (
                      <Tooltip title={<Localized id="meeting-minutes-no-process-topic">_No process</Localized>}>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>

              {expandedGroups.includes(String(processId)) &&
                rows.map(row => (
                  <React.Fragment key={row.id}>
                    {renderRow(row)}
                  </React.Fragment>
                ))
              }
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderRow = (row: any) => {
    const isExpanded = expandedRows.includes(row.id);
    const filteredTasks = row.tasks
      .filter(task =>
        (!showHighPriority || task.priority === 'H') &&
        (!showActiveOnly || task.status === 'ACTIVE' || task.status === 'NEW')
      )
      .sort((a, b) => a.no - b.no);

    return (
      <>
        <TableRow
          hover
          selected={selectedRowId === row.id}
          onClick={() => handleRowClick(row)}
          sx={{
            backgroundColor: row.status === 'FINISHED' ? neutralSurface : 'inherit',
          }}
        >
          <TableCell padding="none" sx={{ width: "10px", maxWidth: "10px" }}>
            <IconButton onClick={(e) => { e.stopPropagation(); handleExpandClick(row.id); }}>
              {row.tasks.length > 0 ? (
                isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />
              ) : (
                <RemoveIcon fontSize="small" />
              )}
            </IconButton>
          </TableCell>
          <TableCell padding="none" sx={{ width: "20px", maxWidth: "20px" }}>
            <ImportContactsIcon fontSize="small" color={row.status === 'FINISHED' ? 'disabled' : 'success'} />
          </TableCell>
          <TableCell sx={{ paddingLeft: "12px" }}>
            {row.name}
          </TableCell>
          <TableCell>
            {row.deadline ? parseAndFormatISODateWithTodayCompare(row.deadline, false, row.status !== 'FINISHED') : ''}
          </TableCell>
          <TableCell>
            {row.responsiblePerson}
          </TableCell>
          <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
            <MeetingMinutesItemStatusLabel status={row.status} />
          </TableCell>
          <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
            <MeetingMinutesItemPriorityLabel priority={row.priority} />
          </TableCell>
        </TableRow>

        {isExpanded && filteredTasks.map((task, index) => (
          <TableRow
            key={task.id + '-task-' + index}
            hover
            selected={selectedRowId === task.id}
            onClick={() => handleRowClick(task)}
            sx={{
              backgroundColor: task.status === 'FINISHED' ? neutralSurface : 'inherit',
            }}
          >
            <TableCell padding="none" sx={{ width: "10px", maxWidth: "10px" }} />
            <TableCell padding="none" sx={{ width: "20px", maxWidth: "20px" }}>
              <TaskIcon fontSize="small" color={task.status === 'FINISHED' ? 'disabled' : 'info'} />
            </TableCell>
            <TableCell>{task.name}</TableCell>
            <TableCell>
              {task.deadline ? parseAndFormatISODateWithTodayCompare(task.deadline, false, task.status !== 'FINISHED') : ''}
            </TableCell>
            <TableCell>{task.responsiblePerson}</TableCell>
            <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
              <MeetingMinutesItemStatusLabel status={task.status} />
            </TableCell>
            <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
              <MeetingMinutesItemPriorityLabel priority={task.priority} />
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };


  // -----------------------------
  // Demo SECTION
  // -----------------------------
  const [attendeanceDialogVisible, setAttendeanceDialogVisible] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting>();

  const attendateDialogClick = useCallback((entity: Meeting) => {
    setSelectedMeeting(entity)
    setAttendeanceDialogVisible(true)
  }, [setAttendeanceDialogVisible])


  const attendeeColumns = [
    { field: 'person', headerName: 'Imię i nazwisko', flex: 1 },
    { field: 'company', headerName: 'Firma', width: 300 },
    { field: 'email', headerName: 'Email', width: 250 }
  ];

  const meetingColumns: GridColDef[] = [
    {
      width: 20,
      sortable: false,
      hideable: false,
      field: 'lineNo',
      headerName: '#',
      flex: 0,
      filterable: false,
      editable: false,
      renderCell: (index) =>
        index.api.getRowIndexRelativeToVisibleRows(index.row.id) + 1,
    },
    {
      field: 'name',
      headerName: l10n.getString("meeting-minutes-meeting-title", null, "Tytuł spotkania"),
      flex: 1,
      minWidth: 200
    },
    {
      field: 'startDate',
      headerName: l10n.getString("meeting-minutes-meeting-start-time", null, "Start spotkania"),
      width: 200,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) {
          return "";
        }
        return format(parseISO(value), 'dd-MM-yyyy HH:mm:ss');
      }
    },
    {
      field: 'endDate',
      headerName: l10n.getString("meeting-minutes-meeting-end-time", null, "Koniec spotkania"),
      width: 200,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) {
          return "";
        }
        return format(parseISO(value), 'dd-MM-yyyy HH:mm:ss');
      }
    },
    {
      field: 'plannedMeetingTime',
      headerName: l10n.getString("meeting-minutes-meeting-planned-time", null, "Planowany czas (minuty)"),
      width: 200
    },
    {
      field: 'duration',
      headerName: l10n.getString("meeting-minutes-meeting-time", null, "Czas (minuty)"),
      width: 150,
      renderCell: (params) => {
        const row = params.row;

        if (!row.startDate || !row.endDate) {
          return '-'
        }
        const start = new Date(row.startDate);
        const end = new Date(row.endDate);
        const meetingLength = end.getTime() - start.getTime();
        const minutes = Math.floor(meetingLength / 60000);
        const seconds = Math.floor((meetingLength % 60000) / 1000);

        const difference = row.plannedMeetingTime * 60000 - meetingLength;
        const diffminutes = Math.floor(Math.abs(difference) / 60000);
        const diffseconds = Math.floor((Math.abs(difference) % 60000) / 1000); //

        const sign = difference > 0 ? "+" : "-"
        const color = difference > 0 ? 'green' : 'red';
        const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        const formattedDifference = `${sign}${diffminutes}:${diffseconds.toString().padStart(2, '0')}`;

        return (
          <div>
            {formattedDuration} <span style={{ color: color }}>({formattedDifference})</span>
          </div>
        );
      }
    },
    {
      field: 'place',
      headerName: l10n.getString("meeting-minutes-meeting-place", null, "Miejsce spotkania"),
      width: 200
    },
    {
      field: 'onDemand',
      headerName: l10n.getString("meeting-minutes-on-demand", null, "Na żądanie"),
      width: 100
    },
    {
      field: 'actions', type: 'actions', width: 200,
      renderCell: (params) => {
        return (
          <ButtonGroup size="small">
            <Button onClick={() => attendateDialogClick(params.row)} color="secondary">
              <Localized id="meeting-minutes-attendees-list">
                <span>Lista obecności</span>
              </Localized>
            </Button>
          </ButtonGroup>
        )
      }
    }
  ];

  const handleCancel = useCallback(() => setAttendeanceDialogVisible(false), [])
  const [emailError, setEmailError] = useState("");

  const validateEmail = () => {
    if (email === "") {
      setEmailError("");
    } else if (!validateEmailUtil(email)) {
      setEmailError("Nieprawidłowy adres e-mail");
    } else {
      setEmailError("");
    }
  };

  const filteredRows = allItems.filter(row =>
    (agendaItems.length === 0 ||
      row.meetingAgendaItemSharedGuid?.toLowerCase() === activeAgendaGuid?.toLowerCase()) &&
    (!showHighPriority || row.priority === 'H') &&
    (!showActiveOnly || row.status === 'ACTIVE' || row.status === 'NEW') &&
    (!selectedProcessId || row.projectProcessId && row.projectProcessId.toString() === selectedProcessId)
  ).sort((a, b) => a.no - b.no);

  const groupedByProcess = useMemo(() => {
    const groups = new Map<string, typeof filteredRows>();
    filteredRows.forEach(row => {
      const key = row.projectProcessId !== null && row.projectProcessId !== undefined
        ? String(row.projectProcessId)
        : 'brak';

      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    });
    return Array.from(groups.entries());
  }, [filteredRows]);


  const [expandedGroups, setExpandedGroups] = useState<string[]>(() =>
    groupedByProcess.map(([processId]) => String(processId))
  );

  const toggleGroupExpand = (processId: string) => {
    setExpandedGroups(prev =>
      prev.includes(processId)
        ? prev.filter(id => id !== processId)
        : [...prev, processId]
    );
  };


  const expandAllGroups = () => {
    const allGroupIds = groupedByProcess.map(([processId]) => String(processId));
    setExpandedGroups(allGroupIds);
  };

  const collapseAllGroups = () => {
    setExpandedGroups([]);
  };


  if (meetingMinute === undefined) {
    return <Skeleton animation="wave" variant="rectangular" height="64vh" ></Skeleton>;
  }

  const emails: Set<Email> = new Set(attendees.map(a => a.email))

  return <>
    <ThemeProvider theme={meetingTheme}>
      <Container maxWidth={false} sx={{
        px: { xs: 1, sm: 2, xl: 3 }, py: 2,
        bgcolor: 'background.default', color: 'text.primary', borderRadius: 3,
        '& .MuiButton-root': { textTransform: 'none', borderRadius: 2, boxShadow: 'none' },
        '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
        '& .MuiTab-root': { textTransform: 'none', minHeight: 54, fontWeight: 500 },
        '& .MuiTableCell-head': { bgcolor: 'background.paper', color: 'text.secondary', fontWeight: 600, py: 1.5 },
        '& .MuiTableCell-root': { borderColor: 'divider', fontSize: 13 },
        '& .MuiTableRow-root.Mui-selected': { bgcolor: seaSurface },
        '& .MuiTableRow-root.Mui-selected:hover': { bgcolor: seaSurface },
        '& .MuiTableBody-root .MuiTableRow-hover': { cursor: 'pointer' },
        '& .MuiTableContainer-root': { boxShadow: 'none', borderRadius: 2 },
      }}>
        <Stack spacing={2.5}>
          {error && <ErrorContainer error={error} />}
          <NewMeetingMinutesHeader meetingMinute={meetingMinute} attendeeCount={attendees.length} saving={saving}
            onCloseMeeting={handleCloseMeetingClick} onSuspendMeeting={handleSuspendMeetingClick}
            onResumeMeeting={handleResumeMeetingClick} />
          {agendaItemIdParam !== null && !requestedAgenda && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Localized id="meeting-minutes-agenda-target-unavailable">
                _Nie znaleziono wskazanego punktu w agendzie bieżącego spotkania.
              </Localized>
            </Alert>
          )}
          {agendaItems.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1} sx={{ mb: 1.5 }}>
                <Typography component="h2" variant="subtitle1" fontWeight={600}>
                  <Localized id="meeting-minutes-meeting-agenda">Agenda spotkania</Localized>
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button size="small" variant="outlined" disabled={activeAgendaIndex <= 0}
                    startIcon={<ChevronLeftIcon />} onClick={() => selectAgenda(agendaItems[activeAgendaIndex - 1].sharedGuid)}>
                    <Localized id="meeting-minutes-agenda-previous">Wstecz</Localized>
                  </Button>
                  <Button size="small" variant="outlined"
                    disabled={activeAgendaIndex >= agendaItems.length - 1}
                    endIcon={<ChevronRightIcon />}
                    onClick={() => selectAgenda(agendaItems[activeAgendaIndex + 1].sharedGuid)}>
                    <Localized id="meeting-minutes-agenda-next">Dalej</Localized>
                  </Button>
                </Stack>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
                {agendaItems.map(item => {
                  const selected = activeAgendaGuid?.toLowerCase() === item.sharedGuid.toLowerCase()
                  const topicCount = allItems.filter(topic =>
                    topic.meetingAgendaItemSharedGuid?.toLowerCase() === item.sharedGuid.toLowerCase()).length
                  return (
                    <ButtonBase key={item.id} aria-current={selected ? "step" : undefined}
                      onClick={() => selectAgenda(item.sharedGuid)}
                      sx={{
                        px: 1.25, py: 1, border: 1, borderColor: selected ? "primary.main" : "divider",
                        borderRadius: 2, bgcolor: selected ? seaSurface : "background.paper",
                        transition: 'background-color 120ms ease', '&:hover': { bgcolor: seaSurface },
                        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                        textAlign: "left", display: "flex", alignItems: "center", gap: 0.75,
                      }}>
                      <AreaStatusChip dotOnly rating={item.rating?.toUpperCase() ?? "NOT_ASSESSED"} />
                      <Typography variant="body2" fontWeight={selected ? 600 : 500} sx={{ overflowWrap: "anywhere", flex: 1, minWidth: 0 }}>
                        {item.order}. {item.name}
                      </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                          <Localized id="meeting-minutes-agenda-topic-count" vars={{ count: topicCount }}>
                            <span>Tematy: {topicCount}</span>
                          </Localized>
                        </Typography>
                    </ButtonBase>
                  )
                })}
              </Box>
            </Paper>
          )}
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", minWidth: 0 }}>
            <TabContext value={tab}>
              <TabList onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                sx={{ px: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: 'divider', '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}>
                <Tab
                  key={0}
                  value="items" icon={<SubjectOutlinedIcon fontSize="small" />} iconPosition="start"
                  label={<Localized id="meeting-minutes-topics">Problemy</Localized>} />
                <Tab
                  key={1}
                  value="attendees" icon={<GroupsOutlinedIcon fontSize="small" />} iconPosition="start"
                  label={<Localized id="meeting-minutes-attendees-list">Lista obecności</Localized>} />

                <Tab
                  key={2}
                  value="attachments" icon={<AttachFileOutlinedIcon fontSize="small" />} iconPosition="start"
                  label={<Localized id="attachments">Załaczniki</Localized>} />
                <Tab
                  key={3}
                  value="meetings" icon={<HistoryOutlinedIcon fontSize="small" />} iconPosition="start"
                  label={<Localized id="meeting-minutes-meetings-history">Historia spotkań</Localized>} />
                <Tab
                  key={4}
                  value="statistics" icon={<BarChartOutlinedIcon fontSize="small" />} iconPosition="start"
                  label={<Localized id="meeting-minutes-meetings-statistics">Statystyki</Localized>} />

              </TabList>
              <TabPanel value="items" sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                      {agendaItems.find(item => item.sharedGuid.toLowerCase() === activeAgendaGuid?.toLowerCase())?.name
                        ?? tr('meeting-minutes-topics', '_Tematy spotkania')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{tr('meeting-refresh-visible-topics', 'Widoczne tematy')}: {filteredRows.length}</Typography>
                  </Box>
                  <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                    {!detailPanelOpen && <Button size="small" variant="outlined" startIcon={<SubjectOutlinedIcon />} onClick={() => setDetailPanelOpen(true)}>
                      {tr('meeting-refresh-show-details', 'Pokaż szczegóły')}
                    </Button>}
                    <Button size="small" disabled={!isEditable} variant="contained" startIcon={<AddIcon />} onClick={openCreateItemDialog}>
                      <Localized id="meeting-minutes-add-topic">Dodaj temat</Localized>
                    </Button>
                  </Stack>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: detailPanelOpen ? 'minmax(0, 1.15fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ p: 1.5, mb: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: neutralSurface }}>
                      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.5} alignItems="center" justifyContent="space-between">
                        <TextField select size="small" label={tr('process', 'Proces')} value={selectedProcessId ?? ''} onChange={handleSelectedProcessChange} sx={{ minWidth: 180, flex: '1 1 180px' }}>
                          <MenuItem value=""><Localized id="all">Wszystkie</Localized></MenuItem>
                          <Divider />
                          <ListSubheader><Localized id="processes">Procesy</Localized></ListSubheader>
                          {processes.map(process => <MenuItem key={process.id} value={process.id}>{process.name}</MenuItem>)}
                        </TextField>
                        <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, value) => value && setViewMode(value)}
                          sx={{ '& .MuiToggleButton-root': { borderColor: 'divider' }, '& .MuiToggleButton-root.Mui-selected': { bgcolor: seaSurface, color: 'primary.main' } }}>
                          <ToggleButton value="flat" aria-label={tr('meeting-minutes-list-view-tooltip', 'Widok listy')}><ViewListIcon fontSize="small" /></ToggleButton>
                          <ToggleButton value="grouped" aria-label={tr('meeting-minutes-grouped-view-tooltip', 'Pogrupuj według procesu')}><ViewAgendaIcon fontSize="small" /></ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                        <FormControlLabel sx={{ mr: 1 }} control={<Switch checked={showActiveOnly} onChange={handleShowActiveChange} size="small" />} label={<Typography variant="body2"><Localized id="meeting-minutes-show-all-active">W trakcie</Localized></Typography>} />
                        <FormControlLabel sx={{ mr: 0 }} control={<Switch checked={showHighPriority} onChange={handleShowHighPriorChange} size="small" />} label={<Typography variant="body2"><Localized id="high-priority">Wysoki priorytet</Localized></Typography>} />
                      </Stack>
                      {viewMode === 'grouped' && <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Button size="small" color="inherit" onClick={expandAllGroups}>{tr('meeting-refresh-expand-all', 'Rozwiń grupy')}</Button>
                        <Button size="small" color="inherit" onClick={collapseAllGroups}>{tr('meeting-refresh-collapse-all', 'Zwiń grupy')}</Button>
                      </Stack>}
                    </Box>
                    {filteredRows.length === 0 && <Box sx={{ p: 3, mb: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                      <SubjectOutlinedIcon color="action" sx={{ mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">{tr('meeting-refresh-no-results', 'Brak tematów dla wybranej agendy i filtrów.')}</Typography>
                    </Box>}
                    {viewMode === 'flat' && (
                      <TableContainer component={Paper}
                        sx={{
                          maxHeight: { xs: 420, xl: 620 },
                        }}>
                        <Table stickyHeader size="small" sx={{ minWidth: 740 }}>
                          <TableHead>
                            <TableRow>

                              <TableCell id="bt" />
                              <TableCell id="type" padding="none" >
                                <Localized id='type'>Typ</Localized>
                              </TableCell>
                              <TableCell id="title" sx={{ paddingLeft: "12px" }}>
                                <Localized id='meeting-minutes-topic-issue'>Temat/Problem</Localized>
                              </TableCell>
                              <TableCell id="process" sx={{ paddingLeft: "12px" }}>
                                <Localized id='process'>Proces</Localized>
                              </TableCell>
                              <TableCell id="Termin" sx={{ minWidth: "150px", width: "150px", maxWidth: "150px" }}>
                                <Localized id='deadline'>Termin</Localized>
                              </TableCell>
                              <TableCell id="Odpowiedzialny" sx={{ minWidth: "150px", width: "150px", maxWidth: "150px" }} >
                                <Localized id='responsible'>Odpowiedzialny</Localized>
                              </TableCell>
                              <TableCell id="priority" sx={{ minWidth: 84, width: 84, px: 1 }}>
                                <Localized id="status">Status</Localized>
                              </TableCell>
                              <TableCell id="status" sx={{ minWidth: 84, width: 84, px: 1 }}>
                                <Localized id="priority">Priorytet</Localized>
                              </TableCell>

                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredRows.map((row) =>
                              <React.Fragment key={row.id}>
                                <TableRow hover key={row.id} selected={selectedRowId === row.id} onClick={() => handleRowClick(row)}
                                  sx={{
                                    backgroundColor: row.status === 'FINISHED' ? neutralSurface : 'inherit',
                                  }}>
                                  <TableCell padding="none" sx={{ width: "10px", maxWidth: "10px" }}>
                                    <IconButton aria-label={tr("meeting-refresh-expand-tasks", "Pokaż lub ukryj zadania")} onClick={event => { event.stopPropagation(); handleExpandClick(row.id); }} >
                                      {row.tasks.length > 0 ?
                                        (expandedRows.includes(row.id) ? <ExpandMoreIcon /> : <ChevronRightIcon />) :
                                        <RemoveIcon fontSize="small" />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell padding="none" sx={{ width: "20px", maxWidth: "20px" }}>
                                    <ImportContactsIcon fontSize="small" color={row.status === 'FINISHED' ? 'disabled' : 'success'} />
                                  </TableCell>
                                  <TableCell sx={{ paddingLeft: "12px" }}>
                                    {row.name}
                                  </TableCell>
                                  <TableCell>
                                    {findProcessName(row.projectProcessId)}
                                  </TableCell>
                                  <TableCell>
                                    {row.deadline ? parseAndFormatISODateWithTodayCompare(row.deadline, false, row.status !== 'FINISHED') : ''}
                                  </TableCell>
                                  <TableCell>
                                    {row.responsiblePerson}
                                  </TableCell>
                                  <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
                                    <MeetingMinutesItemStatusLabel status={row.status} />
                                  </TableCell >
                                  <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
                                    <MeetingMinutesItemPriorityLabel priority={row.priority} />
                                  </TableCell>
                                </TableRow>
                                {expandedRows.includes(row.id) && (
                                  <>
                                    {
                                      row.tasks
                                        .filter(row =>
                                          (!showHighPriority || row.priority === 'H') &&
                                          (!showActiveOnly || row.status === 'ACTIVE' || row.status === 'NEW')
                                        ).sort((a, b) => a.no - b.no)
                                        .map((task, index) => (
                                          <TableRow hover selected={selectedRowId === task.id} onClick={() => handleRowClick(task)} key={task.id + '-task-' + index}
                                            sx={{
                                              backgroundColor: task.status === 'FINISHED' ? neutralSurface : 'inherit',
                                            }}>
                                            <TableCell padding="none" sx={{ width: "10px", maxWidth: "10px" }}></TableCell>
                                            <TableCell padding="none" sx={{ width: "20px", maxWidth: "20px" }}>
                                              <TaskIcon fontSize="small" color={task.status === 'FINISHED' ? 'disabled' : 'info'} />
                                            </TableCell>
                                            <TableCell>{task.name}</TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>
                                              {task.deadline ? parseAndFormatISODateWithTodayCompare(task.deadline, false, task.status !== 'FINISHED') : ''}
                                            </TableCell>
                                            <TableCell>{task.responsiblePerson}</TableCell>
                                            <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
                                              <MeetingMinutesItemStatusLabel status={task.status} />
                                            </TableCell>

                                            <TableCell sx={{ width: "30px", maxWidth: "30px", padding: "2px!important" }}>
                                              <MeetingMinutesItemPriorityLabel priority={task.priority} />
                                            </TableCell>
                                          </TableRow>
                                        ))
                                    }
                                  </>
                                )}
                              </React.Fragment>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {viewMode === 'grouped' && (
                      renderGroupedTable()
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, display: detailPanelOpen ? 'block' : 'none' }}>
                    <NewMeetingItemDetails
                      ref={detailsRef}
                      item={selectedRow}
                      isEditable={isEditable}
                      attendees={attendees}
                      processes={processes}
                      onSaved={handleItemSaved}
                      onAddTask={item => {
                        setSelectedRow(item)
                        setCreateTaskDialogVisible(true)
                      }}
                      onDelete={item => {
                        setSelectedRow(item)
                        setDeleteItemDialogVisible(true)
                      }}
                      onClose={() => setDetailPanelOpen(false)}
                    />
                  </Box>
                </Box>
              </TabPanel>
              <TabPanel value="meetings" sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <DataGrid
                  style={{ minHeight: 100, height: 500 }}
                  rows={meetingMinute.meetings}
                  columns={meetingColumns}
                  initialState={{
                    sorting: { sortModel: [{ field: 'startDate', sort: 'desc' }] }
                  }}
                />
              </TabPanel>
              <TabPanel value="attachments" sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Attachements
                  resourceId={meetingMinute.id}
                  resourceType={BlobResourceType.MeetingMinutes}
                  parentResourceId={meetingMinute.parentObjectGuid}
                  parentResourceType={meetingMinute.parentObjectType === "PROCESS" ? BlobResourceType.ProjectProcess : BlobResourceType.Project}
                  canDelete={true}
                  canUpload={true}
                />
              </TabPanel>
              <TabPanel value="statistics" sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <MeetingMinutesStatistics
                  meetingMinuteId={meetingMinute.id}
                />
              </TabPanel>
              <TabPanel value="attendees" sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between" sx={{ mb: 2.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}><Localized id="meeting-minutes-attendees-list">Lista obecności</Localized></Typography>
                    <Chip size="small" label={attendees.length} sx={{ bgcolor: seaSurface }} />
                  </Stack>
                  {isEditable && <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                    <LoadingButton loading={saving} variant="outlined" startIcon={<GroupsOutlinedIcon />} onClick={() => setAppUsersDialogOpen(true)}>
                      {tr('meeting-refresh-users', 'Z listy użytkowników')}
                    </LoadingButton>
                    <Button startIcon={manualAttendeeOpen ? <CloseIcon /> : <AddIcon />} aria-expanded={manualAttendeeOpen} onClick={() => setManualAttendeeOpen(open => !open)}>
                      {manualAttendeeOpen ? tr('meeting-refresh-hide-form', 'Zamknij formularz') : tr('meeting-refresh-add-manually', 'Dodaj ręcznie')}
                    </Button>
                  </Stack>}
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: manualAttendeeOpen && isEditable ? 'minmax(0, 1fr) minmax(320px, 0.8fr)' : 'minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
                  <List disablePadding sx={{ minWidth: 0 }}>
                    {attendees.map(attendee => <ListItem divider key={attendee.id} sx={{ pl: 0, pr: 7, py: 1.5 }} secondaryAction={isEditable &&
                      <IconButton aria-label={`${tr('delete', 'Usuń')}: ${attendee.person}`} disabled={saving || attendee.email === lastMeeting?.createdBy} onClick={() => setDeleteAttendee({ tag: 'delete', id: attendee.id })}><DeleteIcon fontSize="small" /></IconButton>}>
                      <ListItemAvatar sx={{ minWidth: 50 }}><Avatar sx={{ width: 36, height: 36, bgcolor: seaSurface, color: 'primary.main', fontSize: 13 }}>
                        {attendee.person.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase()}
                      </Avatar></ListItemAvatar>
                      <ListItemText primary={attendee.person} secondary={[attendee.company, attendee.email].filter(Boolean).join(' · ')}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500, sx: { overflowWrap: 'anywhere' } }} secondaryTypographyProps={{ variant: 'caption', sx: { overflowWrap: 'anywhere' } }} />
                    </ListItem>)}
                    {!attendees.length && <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}><Localized id="meeting-minutes-no-attendees">Brak uczestników</Localized></Typography>}
                  </List>
                  {isEditable && manualAttendeeOpen && <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{tr('meeting-refresh-add-attendee', 'Dodaj uczestnika')}</Typography>
                      <TextField required fullWidth size="small" helperText={attendeesPersonError && <RequiredError />} error={attendeesPersonError}
                        label={tr('meeting-minutes-attendee-fullname', 'Imię i nazwisko')} value={person} onChange={e => setPerson(e.target.value)} disabled={saving} />
                      <TextField required fullWidth size="small" helperText={attendeesCompanyError && <RequiredError />} error={attendeesCompanyError}
                        label={tr('meeting-minutes-attendee-company', 'Firma')} value={company} onChange={e => setCompany(e.target.value)} disabled={saving} />
                      <TextField fullWidth size="small" label="Email" type="email" onBlur={validateEmail} error={!!emailError} helperText={emailError}
                        value={email} onChange={e => setEmail(e.target.value)} disabled={saving} />
                      <LoadingButton loading={saving} variant="contained" startIcon={<AddIcon />} onClick={handleAddAttendeeToMeeting} sx={{ alignSelf: 'flex-start' }}><Localized id="add">Dodaj</Localized></LoadingButton>
                    </Stack>
                  </Paper>}
                </Box>
              </TabPanel>
            </TabContext>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: theme => theme.zIndex.appBar,

              p: 1.5,
              borderRadius: 2.5,
              bgcolor: 'background.paper',
              borderColor: 'divider',
              boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.10)',

              '@media print': {
                position: 'static',
                boxShadow: 'none',
              },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap" gap={1.5}>
              <Button onClick={handleBackButtonClick} color="inherit" startIcon={<ChevronLeftIcon />}><Localized id="back">Wróć</Localized></Button>
              <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                {isOperationalDashboard ? (
                  <Tooltip title={dashboardGuid ? "" : <Localized id="meeting-minutes-dashboard-report-unavailable">_Brak identyfikatora dashboardu potrzebnego do wygenerowania raportu.</Localized>}>
                    <span>
                      <ButtonGroup orientation="horizontal" variant="outlined" size="small" color="inherit"
                        sx={{ '& .MuiButton-root': { width: 250, justifyContent: 'flex-start' } }}>
                        <Button disabled={!dashboardGuid} onClick={() => handlePrintOperationalReportClick()} startIcon={<PrintOutlinedIcon />}>
                          <Localized id="operational-dashboard-operational-report">_Raport operacyjny</Localized>
                        </Button>
                        <Button disabled={!dashboardGuid} onClick={() => handlePrintOperationalReportClick(true)} startIcon={<HistoryOutlinedIcon />}>
                          <Localized id="operational-dashboard-operational-report-with-history">_Raport operacyjny z historią</Localized>
                        </Button>
                      </ButtonGroup>
                    </span>
                  </Tooltip>
                ) : <>
                  <Button color="inherit" onClick={handlePrintReportClick} startIcon={<PrintOutlinedIcon />}><Localized id="report-general">Wydruk zbiorczy</Localized></Button>
                  <Button color="inherit" onClick={handlePrintMeetingReportClick} startIcon={<PrintOutlinedIcon />}><Localized id="report-meeting">Wydruk spotkania</Localized></Button>
                </>}
                {/* <MeetingMinutesReportsDropdown meetingMinuteId={meetingMinute.id} /> */}
                {canWrite && <Button variant="contained" onClick={handleShowSendReportDialog} startIcon={<EmailIcon />}><Localized id="report-send-to-attendees">Wyślij raport do uczestników</Localized></Button>}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </ThemeProvider>
    {printDialogOpen && dashboardGuid && (
      <ReportPrinter documentId={dashboardGuid} language={locale} mode="open"
        reportPath={`api/reports/operational-report/${encodeURIComponent(dashboardGuid)}${printWithHistory ? '?withHistory=true' : ''}`}
        isDialogOpen={printDialogOpen} closeDialog={() => setPrintDialogOpen(false)} />
    )}
    {/* Dialogi dziedziczą główny motyw aplikacji, a nie lokalny morski. */}
    <Dialog open={createItemDialogVisible === true} maxWidth="md" fullWidth keepMounted={false}>
      <CreateMeetingMinutesItem
        meetingMinutesId={String(meetingMinute.id)}
        meetingAgendaItemSharedGuid={agendaItems.length > 0 ? activeAgendaGuid : undefined}
        processes={processes}
        onSucces={onSuccess}
        onCancel={handleHideCreateDialog}
      />
    </Dialog>
    <Dialog open={attendeanceDialogVisible === true} maxWidth="md" fullWidth keepMounted={false}>
      <DialogTitle>Lista obecnosci</DialogTitle>
      <DialogContent>
        <DataGrid
          rows={selectedMeeting?.attendanceList as Attendee[]}
          autoHeight
          columns={attendeeColumns}
        />
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleCancel}>
          <Localized id='save'>OK</Localized>
        </LoadingButton>
        <LoadingButton onClick={handleCancel}><Localized id='cancel'>Anuluj</Localized></LoadingButton>
      </DialogActions>
    </Dialog>
    <Dialog open={createTaskDialogVisible === true} maxWidth="md" fullWidth keepMounted={false}>
      <AddItemTaskDialog
        parentRow={selectedRow as MeetingMinuteItem}
        onSucces={onSuccess}
        onCancel={handleAddTaskCancel}
      />
    </Dialog>
    <Dialog open={deleteItemDialogVisible}>
      <DialogContent>
        <DialogContentText>
          <Localized id="confirm-delete">
            <span>Czy napewno chcesz usunąć?</span>
          </Localized>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={saving} onClick={handleConfirmDelItemClick}>
          <Localized id="yes"><span>Tak</span></Localized>
        </LoadingButton>
        <LoadingButton loading={saving} onClick={handleCancelDelClick}>
          <Localized id="no"><span>Nie</span></Localized>
        </LoadingButton>
      </DialogActions>
    </Dialog>
    <Dialog open={showSendGeneralReportDialog} fullWidth maxWidth='md'>
      <DialogTitle>
        <Localized id='meeting-minutes-send-report-meeting'>meeting-minutes-send-report-meeting</Localized>
      </DialogTitle>
      <DialogContent>
        <EmailInput
          meetingMinute={meetingMinute}
          processes={isOperationalDashboard ? [] : processes}
          defaultAttachNoteAttachments={true}
          onListOfRecipientsChange={handleListOfRecipientsChanged}
          onAttachNoteAttachmentsChanged={handleAttachNoteAttachmentsChanged}
          onReportRequestParametersChange={setReportRequestParameters}
          operationalDashboardReport={isOperationalDashboard}
        />
      </DialogContent>
      <DialogActions>
        <LoadingButton
          loading={saving}
          size="small"
          color="success"
          variant="contained"
          startIcon={<EmailIcon />}
          onClick={handleConfirmSendGeneralReport}>
          <Localized id='send'>Ok</Localized>
        </LoadingButton>
        <LoadingButton loading={saving} color="primary" size="small" variant="outlined" onClick={handleCancelSendGeneralReportClick}>
          <Localized id='cancel'>Anuluj</Localized>
        </LoadingButton>
      </DialogActions>
    </Dialog>
    <Dialog open={showSuspendMeetingDialog} fullWidth maxWidth='xs'>
      <DialogTitle>
        <Localized id='meeting-minutes-suspend-meeting-question'>Czy zawiesić spotkanie?</Localized>
      </DialogTitle>
      <DialogContent>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={saving} onClick={handleConfirmSuspendMeetingClick}><Localized id='ok'>Ok</Localized></LoadingButton>
        <LoadingButton loading={saving} onClick={handleSuspenCloseMeetingClick}><Localized id='cancel'>Anuluj</Localized></LoadingButton>
      </DialogActions>
    </Dialog>
    <Dialog open={showResumeMeetingDialog} fullWidth maxWidth='xs'>
      <DialogTitle>
        <Localized id='meeting-minutes-resume-meeting-question'>Czy przywrócić spotkanie?</Localized>
      </DialogTitle>
      <DialogContent>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={saving} onClick={handleConfirmResumeMeetingClick}><Localized id='ok'>Ok</Localized></LoadingButton>
        <LoadingButton loading={saving} onClick={handleResumeCloseMeetingClick}><Localized id='cancel'>Anuluj</Localized></LoadingButton>
      </DialogActions>
    </Dialog>
    {showCloseMeetingDialog && <CloseMeetingDialog meetingMinutesId={meetingMinute.id} meetingMinute={meetingMinute}
      processes={processesLoaded ? processes : undefined} beforeConfirm={async () => (await detailsRef.current?.flush()) !== false}
      onClose={() => setShowCloseMeetingDialog(false)}
      onClosed={closed => { setMeetingMinute(closed); setShowCloseMeetingDialog(false) }} />}
    <Dialog open={deleteAttendee.tag === 'delete'}>
      <DialogTitle>
        <Localized id='delete'>Usuń</Localized>
      </DialogTitle>
      <DialogContent>
        <Localized id='confirm-delete'>Czy napewno chcesz usunąć?</Localized>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleConfirmDeleteAttendee}>
          <Localized id='ok'>Ok</Localized>
        </Button>
        <Button onClick={() => setDeleteAttendee({ tag: 'none' })}>
          <Localized id='cancel'>Anuluj</Localized>
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={appUsersDialogOpen} onClose={() => setAppUsersDialogOpen(false)} fullWidth maxWidth='lg'>
      <DialogTitle>Użytkownicy</DialogTitle>
      <DialogContent>
        <UsersBrowser gridRef={usersGridRef} disabled={emails} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAddAppUsers}>
          <Localized id='ok'>OK</Localized>
        </Button>
        <Button onClick={() => setAppUsersDialogOpen(false)}>
          <Localized id='cancel'>Anuluj</Localized>
        </Button>
      </DialogActions>
    </Dialog>
  </>
}
